/**
 * LUKHAS.ID Authentication API Client
 *
 * Handles all authentication operations including:
 * - JWT token management (secure: access tokens in memory, refresh in HttpOnly cookies)
 * - WebAuthn/passkey authentication
 * - OAuth2/OIDC flows
 * - Session management
 *
 * Security model:
 * - Access tokens: stored in memory only, short-lived (15 min)
 * - Refresh tokens: HttpOnly cookies, server-managed, rotated on use
 * - No sensitive tokens exposed to JavaScript (XSS protection)
 */

import {
  setAccessToken,
  clearAuth,
  authRequest,
  authFetch,
  initializeAuth as initAuth,
  logout as clientLogout,
} from './authClient'

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL || 'https://api.lukhas.id/v1'

export interface User {
  id: string
  email: string
  username: string
  displayName: string
  avatar?: string
  createdAt: string
  // Auth status
  emailVerified?: boolean
  verified?: boolean  // Alias for emailVerified (used in demo mode)
  mfaEnabled?: boolean
  webAuthnEnabled?: boolean
  // Authorization
  roles?: string[]
  tier?: string
}

export interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresIn: number
  tokenType: 'Bearer'
}

export interface LoginCredentials {
  // Identifier - either email or ΛiD (one required)
  email?: string
  lambdaId?: string
  // Auth method - either password or passphrase (one required)
  password?: string
  passphrase?: string
  // Options
  rememberMe?: boolean
}

export interface RegisterData {
  email: string
  username: string
  password: string
  displayName?: string
  acceptTerms: boolean
}

export interface MFAChallenge {
  type: 'totp' | 'webauthn' | 'email'
  challengeId: string
  expiresAt: string
}

export interface WebAuthnCredential {
  id: string
  publicKey: string
  counter: number
  createdAt: string
  lastUsed?: string
  device?: string
}

/**
 * Authentication API Client
 *
 * Uses secure token management from authClient.ts:
 * - Access tokens in memory only
 * - Refresh tokens in HttpOnly cookies
 * - Automatic token refresh on 401
 */
class AuthAPI {
  private baseURL: string

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL
  }

  /**
   * Initialize auth state from server session
   * Call on app startup to restore session from HttpOnly cookie
   */
  async initializeSession(): Promise<boolean> {
    return initAuth()
  }

  /**
   * Handle successful auth response - store access token in memory
   * Server will set refresh token as HttpOnly cookie
   */
  private handleAuthSuccess(tokens: AuthTokens): void {
    setAccessToken(tokens.accessToken, tokens.expiresIn)
  }

  /**
   * Make authenticated API request
   * Uses authRequest from authClient for automatic token management
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    return authRequest<T>(endpoint, options)
  }

  /**
   * Make unauthenticated request (for login/register before auth)
   */
  private async unauthenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for server to set HttpOnly refresh token
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText
      }))
      throw new Error(error.message || `API Error: ${response.status}`)
    }

    return response.json()
  }

  /**
   * Register new user
   * Server sets HttpOnly refresh cookie, we store access token in memory
   */
  async register(data: RegisterData): Promise<{ user: User; tokens: AuthTokens }> {
    const result = await this.unauthenticatedRequest<{ user: User; tokens: AuthTokens }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    })

    this.handleAuthSuccess(result.tokens)
    return result
  }

  /**
   * Login with email and password
   * Server sets HttpOnly refresh cookie on success
   */
  async login(credentials: LoginCredentials): Promise<{ user: User; tokens: AuthTokens; mfaRequired?: MFAChallenge }> {
    const result = await this.unauthenticatedRequest<{ user: User; tokens: AuthTokens; mfaRequired?: MFAChallenge }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })

    if (!result.mfaRequired) {
      this.handleAuthSuccess(result.tokens)
    }

    return result
  }

  /**
   * Complete MFA challenge
   */
  async completeMFA(challengeId: string, code: string): Promise<{ user: User; tokens: AuthTokens }> {
    const result = await this.unauthenticatedRequest<{ user: User; tokens: AuthTokens }>('/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ challengeId, code }),
    })

    this.handleAuthSuccess(result.tokens)
    return result
  }

  /**
   * Logout current user
   * Clears in-memory token and server clears HttpOnly cookie
   */
  async logout(): Promise<void> {
    await clientLogout()
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<User> {
    return this.request<User>('/auth/me')
  }

  /**
   * Refresh access token using HttpOnly cookie
   * Handled automatically by authClient, exposed for manual refresh if needed
   */
  async refreshToken(): Promise<void> {
    // Refresh is handled by authClient using HttpOnly cookie
    // This just re-initializes the session
    const success = await initAuth()
    if (!success) {
      throw new Error('Session expired or invalid')
    }
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return this.request('/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return this.request('/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    })
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<{ user: User }> {
    return this.request('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  }

  /**
   * Resend verification email
   */
  async resendVerificationEmail(): Promise<{ message: string }> {
    return this.request('/auth/verify-email/resend', {
      method: 'POST',
    })
  }

  // ============================================================================
  // WebAuthn / Passkey Methods
  // ============================================================================

  /**
   * Begin WebAuthn registration
   */
  async beginWebAuthnRegistration(): Promise<PublicKeyCredentialCreationOptions> {
    return this.request('/auth/webauthn/register/begin', {
      method: 'POST',
    })
  }

  /**
   * Complete WebAuthn registration
   */
  async completeWebAuthnRegistration(credential: any): Promise<{ credential: WebAuthnCredential }> {
    return this.request('/auth/webauthn/register/complete', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    })
  }

  /**
   * Begin WebAuthn authentication
   */
  async beginWebAuthnAuthentication(email?: string): Promise<PublicKeyCredentialRequestOptions> {
    return this.request('/auth/webauthn/authenticate/begin', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  /**
   * Complete WebAuthn authentication
   * Server sets HttpOnly refresh cookie on success
   */
  async completeWebAuthnAuthentication(
    credential: any
  ): Promise<{ user: User; tokens: AuthTokens }> {
    const result = await this.unauthenticatedRequest<{ user: User; tokens: AuthTokens }>('/auth/webauthn/authenticate/complete', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    })

    this.handleAuthSuccess(result.tokens)
    return result
  }

  /**
   * List user's WebAuthn credentials
   */
  async listWebAuthnCredentials(): Promise<WebAuthnCredential[]> {
    return this.request('/auth/webauthn/credentials')
  }

  /**
   * Remove WebAuthn credential
   */
  async removeWebAuthnCredential(credentialId: string): Promise<void> {
    return this.request(`/auth/webauthn/credentials/${credentialId}`, {
      method: 'DELETE',
    })
  }

  // ============================================================================
  // Apple Sign In Methods
  // ============================================================================

  /**
   * Exchange an Apple session token for a user profile.
   * Called after the Apple JS SDK popup flow returns a backend session token.
   */
  async loginWithAppleToken(sessionToken: string): Promise<User> {
    // Store as the active access token so getCurrentUser() works
    setAccessToken(sessionToken, 900) // 15-minute default
    return this.getCurrentUser()
  }

  // ============================================================================
  // OAuth2 / OIDC Methods
  // ============================================================================

  /**
   * Get OAuth2 authorization URL
   */
  getOAuthURL(provider: 'google' | 'github' | 'microsoft', redirectUri: string): string {
    const params = new URLSearchParams({
      provider,
      redirect_uri: redirectUri,
      client_id: 'lukhas_web',
    })
    return `${this.baseURL}/oauth/authorize?${params}`
  }

  /**
   * Exchange OAuth code for tokens
   * Server sets HttpOnly refresh cookie on success
   */
  async exchangeOAuthCode(
    code: string,
    provider: string
  ): Promise<{ user: User; tokens: AuthTokens }> {
    const result = await this.unauthenticatedRequest<{ user: User; tokens: AuthTokens }>('/oauth/callback', {
      method: 'POST',
      body: JSON.stringify({ code, provider }),
    })

    this.handleAuthSuccess(result.tokens)
    return result
  }

  /**
   * Get SSO redirect URL for a domain
   */
  getSSORedirectURL(domain: string, returnTo?: string): string {
    const params = new URLSearchParams({
      domain,
      return_to: returnTo || window.location.origin,
    })
    return `${this.baseURL}/sso/redirect?${params}`
  }

  /**
   * Validate SSO token from another LUKHAS domain
   * Server sets HttpOnly refresh cookie on success
   */
  async validateSSOToken(token: string): Promise<{ user: User; tokens: AuthTokens }> {
    const result = await this.unauthenticatedRequest<{ user: User; tokens: AuthTokens }>('/sso/validate', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })

    this.handleAuthSuccess(result.tokens)
    return result
  }

  // ============================================================================
  // User Profile Methods
  // ============================================================================

  /**
   * Update user profile
   */
  async updateProfile(data: Partial<User>): Promise<User> {
    return this.request('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Upload avatar
   * Uses authFetch for automatic token handling with FormData
   */
  async uploadAvatar(file: File): Promise<{ avatarUrl: string }> {
    const formData = new FormData()
    formData.append('avatar', file)

    // Use authFetch for automatic token handling
    // Note: Don't set Content-Type header - browser will set it with boundary for FormData
    const resp = await authFetch(`${this.baseURL}/users/me/avatar`, {
      method: 'POST',
      body: formData,
    })

    if (!resp.ok) {
      const error = await resp.json().catch(() => ({ message: resp.statusText }))
      throw new Error(error.message || `Upload failed: ${resp.status}`)
    }

    return resp.json()
  }

  /**
   * Delete user account
   */
  async deleteAccount(password: string): Promise<void> {
    await this.request('/users/me', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    })
    clearAuth()
  }
}

// Export singleton instance
export const authAPI = new AuthAPI()

// Export class for testing
export default AuthAPI
